import type { LifecyclePort } from '@elara/client-core/lib/lifecycle-port';

/**
 * Web `LifecyclePort` implementation (ELARA-695).
 *
 * Maps the platform-neutral "app resumed" signal onto the two DOM events that
 * indicate a tab has come back to the foreground or regained connectivity:
 * `visibilitychange` (when `document.visibilityState` flips to `visible`) and
 * `online`. React Native composes its own `AppState`/NetInfo-backed port behind
 * the same contract at its own composition root.
 *
 * A missing global (SSR, hardened/locked-down contexts) degrades to a no-op
 * subscription rather than throwing, so callers can wire the port
 * unconditionally.
 *
 * This file is web-only and must not be imported by `@elara/mobile`.
 */
export function createWebLifecyclePort(): LifecyclePort {
  return {
    onResume(listener) {
      if (typeof document === 'undefined' || typeof window === 'undefined') {
        return () => {};
      }

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') listener();
      };
      const handleOnline = () => {
        listener();
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('online', handleOnline);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('online', handleOnline);
      };
    },
  };
}
